import { t } from '@lingui/macro'
import {
    Document,
    Image,
    Page,
    StyleSheet,
    Text,
    View
} from '@react-pdf/renderer'
import { dateFormat } from 'app/appSettings'
import { optionToIndex } from 'app/utils/survey/surveyToRTF'
import { getLabelFromTranslationData } from 'app/views/common/translationsCommon'
import { isNil } from 'lodash'
import moment from 'moment'
import { myI18n } from 'translation/I18nConnectedProvider'
import { commonPDFStyles, pdfDefaultFontSize, PDFPageNumber } from '../forms/pdf-components/PDFCommon'
import { questionTypes } from './QuestionWidget'
import { surveyPDFStyles } from './SurveyScorePdf'
import { SurveyQuestion, SurveySubmittedPdfProps } from './surveyTypes'

const styles = StyleSheet.create({
  page: {
    padding: '2.54cm',
    paddingTop: '3.81cm',
    fontFamily: 'Roboto',
    fontSize: pdfDefaultFontSize
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20
  },
  headerDivider: {
    width: '100%',
    height: 0.5,
    // backgroundColor: '#cccccc',
    marginBottom: 16
  },
  logo: {
    width: 100,
    height: 100,
    marginRight: 10
  },
  headerTextContainer: {
    marginLeft: 0
  },
  headerRow: {
    flexDirection: 'row',
    marginBottom: 4
  },
  headerLabel: {
    fontWeight: 'bold'
  },
  sectionHeader: {
    marginTop: 16,
    marginBottom: 8,
    marginLeft: 16,
    fontWeight: 'bold',
    paddingBottom: 4
  },
  sectionDivider: {
    width: '100%',
    height: 0.5,
    // backgroundColor: '#cccccc',
    marginBottom: 8
  },
  question: {
    marginBottom: 16,
    marginLeft: 0
  },
  questionText: {
    fontWeight: 'bold',
    marginBottom: 6
  },
  questionHeader: {
    fontSize: pdfDefaultFontSize,
    color: '#17374f',
    backgroundColor: '#bfdba0',
    padding: 8,
    border: '1px solid black',
    fontWeight: 'bold',
    marginTop: 0,
    textAlign: 'center',
    marginBottom: 16
  },
  answer: {
    fontSize: pdfDefaultFontSize
    // marginLeft: 8
  },
  answerItem: {
    fontSize: pdfDefaultFontSize,
    // marginLeft: 8,
    marginBottom: 2
  },
  groupItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 4
  },
  groupAnswer: {
    marginLeft: 4,
    fontWeight: 600
  }
})

// Mirrors questionToText from surveyToRTF but returns PDF-compatible strings/arrays
function resolveAnswerText (
  question: SurveyQuestion,
  answer: any,
  langVersion: string,
  otherAnswer: any
): string | string[] {
  const { type, options } = question

  switch (type) {
    case 'SELECT_ONE': {
      if (answer === 'nonApplicable') return '-'
      const index = optionToIndex(answer)
      if (index === null || isNil(options?.[index])) return ''
      let text = getLabelFromTranslationData({ langVersion, data: options?.[index]?.titleValue }) ?? ''
      if (!isNil(otherAnswer)) text += ` (${String(otherAnswer)})`
      return text
    }
    case 'SELECT_MULTIPLE': {
      if (!Array.isArray(answer) || isNil(options)) return isNil(answer) ? '' : String(answer)
      return options
        .map((option, i) => {
          if (isNil(answer[i])) return null
          const text = getLabelFromTranslationData({ langVersion, data: option.titleValue }) ?? ''
          if (!isNil(otherAnswer) && !isNil(otherAnswer[i])) return `${text}: (${String(otherAnswer[i])})`
          return text
        })
        .filter(Boolean) as string[]
    }
    case 'SELECT_YESNO': {
      const index = optionToIndex(answer)
      if (index === null || isNil(options?.[index])) return ''
      try {
        const textObj = options?.[index]?.text
        if (!isNil(textObj) && typeof textObj === 'object' && 'id' in textObj) {
          return myI18n._({ id: textObj.id })
        }
        return isNil(textObj) ? '' : String(textObj)
      } catch {
        return ''
      }
    }
    case 'INPUT_DATE':
      return isNil(answer) ? '' : moment.utc(answer).format(dateFormat)
    case 'INPUT_DATE_RANGE': {
      const parts: string[] = []
      if (!isNil(answer?.start)) parts.push(`${myI18n._(t`Start Date`)}: ${moment.utc(answer.start).format(dateFormat)}`)
      if (!isNil(answer?.end)) parts.push(`${myI18n._(t`End Date`)}: ${moment.utc(answer.end).format(dateFormat)}`)
      return parts.join('\n')
    }
    default:
      if (isNil(otherAnswer)) return isNil(answer) ? '' : String(answer)
      return `${String(answer ?? '')} (${String(otherAnswer)})`
  }
}

function AnswerView ({ resolved }: { resolved: string | string[] }): React.ReactElement {
  if (Array.isArray(resolved)) {
    return (
      <View>
        {resolved.map((item, i) => (
          <Text key={i} style={styles.answerItem}>{item}</Text>
        ))}
      </View>
    )
  }
  return <Text style={styles.answer}>{resolved}</Text>
}

const SurveySubmittedPdf = ({
  survey,
  organization,
  values,
  data,
  disableFields,
  user,
  logoUrl
}: SurveySubmittedPdfProps): React.ReactElement => {
  const langVersion = user?.language ?? 'en'
  const sections: React.ReactElement[] = []

  for (const section of data) {
    let questionCount = 0
    const notAllDisabled = section.questions.some(
      q =>
        !disableFields.includes(q.id) &&
        q.type !== questionTypes.HEADER.id &&
        q.skipInPrint !== true
    )
    const unavailable =
      section.questions.length === 0 ||
      disableFields.includes(section.templateId) ||
      !notAllDisabled

    if (unavailable) continue

    const questionElements: React.ReactElement[] = []

    for (const question of section.questions) {
      const displayQuestion =
        !disableFields.includes(question.id)
      if (!displayQuestion) continue

      const otherAnswer = values[question.id + '_other']

      if (question.type === questionTypes.HEADER.id) {
        questionElements.push(
          <Text key={question.id} style={styles.questionHeader}>
            {question.text}
          </Text>
        )
      } else if (question.isGroup === true) {
        questionCount++
        const answer = values[question.id]
        const groupItems = (question.groupQuestions ?? []).map((gq, i) => {
          const subAnswer = isNil(answer) ? '' : answer[gq.index]
          const otherSubAnswer = isNil(otherAnswer) ? undefined : otherAnswer[gq.index]
          const subLabel = getLabelFromTranslationData({ langVersion, data: gq.titleValue }) ?? ''
          const resolved = resolveAnswerText(question, subAnswer, langVersion, otherSubAnswer)
          return (
            <View key={i} style={styles.groupItem}>
              <View style={commonPDFStyles.bullet} />
              <View style={{ flex: 1 }}>
                <Text>
                  <Text>{subLabel}</Text>
                  {!Array.isArray(resolved) && (
                    <Text style={styles.groupAnswer}> {resolved}</Text>
                  )}
                </Text>
                {Array.isArray(resolved) &&
                  resolved.map((item, j) => (
                    <Text key={j} style={styles.groupAnswer}>{item}</Text>
                  ))}
              </View>
            </View>
          )
        })
        questionElements.push(
          <View key={question.id} style={styles.question} wrap={false}>
            <Text style={styles.questionText}>
              {questionCount}. {question.text}
            </Text>
            {question.tooltip != null &&
              <Text
                style={{
                  fontSize: pdfDefaultFontSize - 2,
                  fontStyle: 'italic',
                  marginBottom: 8
                }}
              >
                {question.tooltip}
              </Text>}
            <View>{groupItems}</View>
          </View>
        )
      } else {
        questionCount++
        const answer = values[question.id]
        const resolved = resolveAnswerText(question, answer, langVersion, otherAnswer)
        questionElements.push(
          <View key={question.id} style={styles.question} wrap={false}>
            <Text style={styles.questionText}>
              {questionCount}. {question.text}
            </Text>
            {question.tooltip != null &&
              <Text
                style={{
                  fontSize: pdfDefaultFontSize - 2,
                  fontStyle: 'italic',
                  marginBottom: 8
                }}
              >
                {question.tooltip}
              </Text>}
            <AnswerView resolved={resolved} />
          </View>
        )
      }
    }

    sections.push(
      <View break={sections.length > 0} key={section.templateId}>
        <Text style={commonPDFStyles.sectionTitle}>{section.header}</Text>
        <View style={styles.sectionDivider} />
        {questionElements}
      </View>
    )
  }

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <View fixed style={{ flexDirection: 'row', left: 0, top: '1.27cm', paddingRight: '1.27cm', position: 'absolute', justifyContent: 'flex-end', width: '100%' }}>
          <Image
            style={surveyPDFStyles.fixedImage}
            src='/assets/images/centre_logo_full.png'
          />
        </View>
        <Text
          style={commonPDFStyles.pdfTitle}
        >
          {survey.title}
        </Text>
        <View style={styles.headerTextContainer}>
          <View style={styles.headerRow}>
            <Text style={styles.headerLabel}>{myI18n._(t`Organization name:`)} </Text>
            <Text>
              {organization?.organisationsName}
            </Text>
          </View>
          <View style={styles.headerRow}>
            <Text style={styles.headerLabel}>{myI18n._(t`Submission date:`)} </Text>
            <Text>
              {moment.utc(survey.modifiedDate).format('YYYY-MM-DD')}
            </Text>
          </View>
        </View>
        <View style={styles.headerDivider} />
        {sections}
        <PDFPageNumber />
      </Page>
    </Document>
  )
}

export default SurveySubmittedPdf
